import { useWeb3React } from '@web3-react/core'
import { BigNumber, utils } from 'ethers'
import { useResponsive } from 'hooks'
import { formatBigNumber, formatTimestamp, getSpacing } from 'utils'
import { Box } from './Box'
import { Button } from './Button'
import { MintingButton } from './MintingButton'
import { Spinner } from './Spinner'
import { BodyText, Text, Title } from './Typography'
import { useStaking } from './useStaking'

interface IRow {
  label: string
  value: string
}

const Row = ({ label, value }: IRow) => (
  <Box margin="6px 0px" flexDirection="row" justifyContent="space-between" alignItems="center">
    <Text color="WHITE_50" size="m">
      {label}
    </Text>
    {typeof value === 'string' ? <Text size="m">{value}</Text> : value}
  </Box>
)

interface IStakeRows {
  stakingTokenSymbol: string
  stakingTokenDecimals: number
  stakingTokenBalance: BigNumber
  stakingTokenStakedBalance: BigNumber
  stakeDeadline: BigNumber
}

const StakeRows = ({
  stakingTokenSymbol,
  stakingTokenBalance,
  stakingTokenStakedBalance,
  stakingTokenDecimals,
  stakeDeadline,
}: IStakeRows) => {
  return (
    <Box width="350px">
      <Row
        label={`$${stakingTokenSymbol} Balance`}
        value={`${formatBigNumber(
          stakingTokenBalance,
          undefined,
          stakingTokenDecimals,
        )} $${stakingTokenSymbol}`}
      />
      <Row
        label={`$${stakingTokenSymbol} Staked`}
        value={`${formatBigNumber(
          stakingTokenStakedBalance,
          undefined,
          stakingTokenDecimals,
        )} $${stakingTokenSymbol}`}
      />
      <Row
        label="Staking Deadline"
        value={
          stakeDeadline.isZero() ? '-' : `${formatTimestamp(Number(stakeDeadline.toString()))}`
        }
      />
    </Box>
  )
}

interface IClaimRows {
  rewardsTokenSymbol: string
  rewardsTokenClaimedBalance: BigNumber
  rewardsTokenRewardsAvailable: BigNumber
  rewardsTokenStopTime: BigNumber
}

const ClaimRows = ({
  rewardsTokenSymbol,
  rewardsTokenClaimedBalance,
  rewardsTokenRewardsAvailable,
  rewardsTokenStopTime,
}: IClaimRows) => {
  return (
    <Box width="350px">
      <Row
        label={`$${rewardsTokenSymbol} Claimed`}
        value={`$${rewardsTokenSymbol} ${formatBigNumber(rewardsTokenClaimedBalance)}`}
      />
      <Row
        label={`$${rewardsTokenSymbol} Claimable`}
        value={`$${rewardsTokenSymbol} ${formatBigNumber(rewardsTokenRewardsAvailable)} `}
      />
      <Row
        label="Reward Stream Ends"
        value={
          rewardsTokenStopTime.isZero()
            ? '-'
            : `${formatTimestamp(Number(rewardsTokenStopTime.toString()))}`
        }
      />
    </Box>
  )
}

export const ClaimInterface = () => {
  const { chainId } = useWeb3React()
  const { width } = useResponsive()
  const isMobile = width < 700
  const {
    stakeDeadline,
    rewardsTokenRewardsAvailable,
    stakingTokenSymbol,
    stakingTokenDecimals,
    stakingTokenBalance,
    stakingTokenStakedBalance,
    needsApproval,
    approveStakingToken,
    stake,
    claim,
    stakingTokenAddress,
    rewardsTokenClaimedBalance,
    rewardsTokenSymbol,
    loadingApprove,
    loadingClaim,
    loadingStake,
    loadingDetails,
    rewardsTokenStopTime,
  } = useStaking()

  const nowInSeconds = () => Math.floor(Date.now() / 1000)

  const stakeDisabled =
    stakingTokenBalance.isZero() || nowInSeconds() > Number(stakeDeadline.toString())

  const claimDisabled =
    rewardsTokenRewardsAvailable.isZero() || nowInSeconds() < Number(stakeDeadline.toString())

  return (
    <Box
      style={{
        width: isMobile ? '350px' : '750px',
      }}
    >
      <Title
        style={{ marginBottom: getSpacing('m') }}
        color="WHITE"
        weight="semi-bold"
        size="xl"
        tag="h1"
      >
        Burn your $KERMAN for $ARCx
      </Title>
      <BodyText style={{ marginBottom: getSpacing('m') }} color="WHITE" weight="thin" size="s">
        As a $KERMAN holder, you are entitled to your share of 1% of the $ARCX supply over the
        course of three years. By claiming and staking before the Staking Deadline, you will receive
        your $ARCX tokens, and your $KERMAN tokens will be burned. Thank you for supporting the work
        we are doing at ARCx.
      </BodyText>
      {loadingDetails ? (
        <Box height="200px" justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      ) : (
        <Box
          alignItems="center"
          justifyContent="space-between"
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <Box marginBottom={isMobile ? getSpacing('m') : '0px'}>
            <StakeRows
              stakingTokenSymbol={stakingTokenSymbol}
              stakingTokenBalance={stakingTokenBalance}
              stakingTokenDecimals={stakingTokenDecimals}
              stakingTokenStakedBalance={stakingTokenStakedBalance}
              stakeDeadline={stakeDeadline}
            />
            <Box marginTop="16px">
              <Button
                disabled={stakeDisabled}
                onClick={needsApproval ? approveStakingToken : stake}
                size="m"
                variant="fancy"
                text={needsApproval ? 'Approve' : 'Stake'}
                loading={loadingApprove || loadingStake}
              />
            </Box>
          </Box>
          <Box>
            <ClaimRows
              rewardsTokenSymbol={rewardsTokenSymbol}
              rewardsTokenClaimedBalance={rewardsTokenClaimedBalance}
              rewardsTokenRewardsAvailable={rewardsTokenRewardsAvailable}
              rewardsTokenStopTime={rewardsTokenStopTime}
            />
            <Box marginTop="16px">
              <Button
                onClick={claim}
                disabled={claimDisabled}
                size="m"
                variant="fancy"
                text="Claim"
                loading={loadingClaim}
              />
            </Box>
          </Box>
        </Box>
      )}
      {chainId === 4 && (
        <Box width="200px" marginTop="12px">
          <MintingButton mintAmount={utils.parseEther('100')} tokenAddress={stakingTokenAddress} />
        </Box>
      )}
    </Box>
  )
}
